import { memo, useCallback, useContext } from 'react';
import * as timeago from 'timeago.js';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import useWarmingSequences from '@/hooks/useWarmingSequences';
import PersonContext from '@/pages/Person/PersonContext';
import AppointmentMinimalDetails from '@/pages/Profile/components/AppointmentMinimalDetails';
import type { Props } from '@/pages/Profile/components/EngagementInformationSection/types';
import NextAppointment from '@/pages/Profile/components/NextAppointment';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import { MobileSystem } from '@/types/person';
import DataList, { DataRow } from '@/ui/components/DataList';

const EngagementInformationSection = ({ nextAppointment, hasOngoingAppointment }: Props) => {
  const { person } = useContext(PersonContext);
  const { next_appointment_attendance, time_zone: timeZone } = person;

  const { refetch: getWarmingSequences } = useWarmingSequences(false);
  const formatWarmingSequences = useCallback(async () => {
    const { data: warmingSequences } = await getWarmingSequences();
    if (warmingSequences) return warmingSequences.map(([value, label]) => ({ label, value }));
    return [];
  }, [getWarmingSequences]);

  return (
    <div>
      <SectionHeading text="Engagement Information" />
      <DataList>
        {/* Messaging */}
        <DataRow label="Messaging">
          <PersonInPlaceEditor
            fields={[
              {
                key: 'settings.channel_sms_enabled',
                label: 'Opted in to SMS',
                type: FieldTypes.BOOLEAN
              },
              {
                key: 'subscribed_to_warming',
                label: 'Subscribed to Warming',
                type: FieldTypes.BOOLEAN
              },
              {
                key: 'warming_sequence',
                label: 'Next Message In Sequence',
                type: FieldTypes.SELECT,
                options: formatWarmingSequences
              }
            ]}>
            {person.settings.channel_sms_enabled ? (
              <>
                <p className="text-sm">
                  <span>Warming: </span>
                  {person.subscribed_to_warming ? (
                    <span className="text-green-600">ON</span>
                  ) : (
                    <span className="text-red-600">OFF</span>
                  )}
                </p>
                {person.warming_sequence_label && (
                  <p className="text-sm">
                    Next Message In Sequence: {person.warming_sequence_label}
                  </p>
                )}
              </>
            ) : (
              <p className="text-red-600">Opted out of SMS</p>
            )}
          </PersonInPlaceEditor>
        </DataRow>

        <DataRow label="App Last Opened Date">
          {person.last_app_open_at ? (
            <span>
              {person?.last_app_open_at &&
                timeago.format(new Date(person.last_app_open_at).toLocaleString())}{' '}
              ({person.last_known_app_version})
            </span>
          ) : (
            'Never Opened'
          )}
        </DataRow>

        {!!person?.last_known_app_version && (
          <DataRow label="App Version">
            <span
              className={person?.last_known_system === MobileSystem.ANDROID ? 'capitalize' : ''}>
              {person.last_known_app_version}
              {!!person?.last_known_system && ` (${person.last_known_system})`}
            </span>
          </DataRow>
        )}

        {/* Ongoing Appointment */}
        {nextAppointment && hasOngoingAppointment && (
          <div id="ongoing-appointment-row">
            <DataRow label="Ongoing Appointment">
              <AppointmentMinimalDetails
                appointmentAttendance={{
                  appointment_start_at: nextAppointment.appointment.start_at || '',
                  appointment_type_name: nextAppointment.appointment.title || '',
                  providers: nextAppointment.appointment.providers,
                  state: nextAppointment.state
                }}
                timeZone={timeZone}
              />
            </DataRow>
          </div>
        )}

        {/* Next Appointment */}
        {next_appointment_attendance && (
          <NextAppointment
            nextAppointmentAttendance={next_appointment_attendance}
            memberTZ={timeZone}
          />
        )}

        <DataRow label="Last Attended Appointment">
          {person.last_non_breathalyzer_appointment_attended ? (
            <AppointmentMinimalDetails
              appointmentAttendance={person.last_non_breathalyzer_appointment_attended}
              timeZone={timeZone}
            />
          ) : (
            'None'
          )}
        </DataRow>

        <DataRow label="Challenges">
          {person.challenges.last_challenge_finished_at ? (
            <>
              {person.challenges.num_challenges_completed} of{' '}
              {person.challenges.num_challenges_available} completed{' '}
              <span className="text-sm">
                (last at{' '}
                {timeago.format(
                  new Date(person.challenges.last_challenge_finished_at).toLocaleString()
                )}
                )
              </span>
            </>
          ) : (
            <span className="text-red-600">No Challenges Marked Completed</span>
          )}
        </DataRow>
      </DataList>
    </div>
  );
};

export default memo(EngagementInformationSection);
